import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPage, updatePage, createPage, getPageSummaries, getMediaUrl, removeMainImage,
  getPageSections, createPageSection, updatePageSection, deletePageSection, reorderPageSections,
  getPageRevision, savePageRevision, discardPageRevision, publishPageRevision,
} from './api';
import { openSitePreview } from './previewLinks';
import ImagePicker from './ImagePicker';
import PagePreviewModal, { previewEnabled } from './PagePreviewModal';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import Badge from './ui/Badge';
import Alert from './ui/Alert';
import ThinBanner from './seo/ThinBanner';
import { useToast } from './Toast';
import { useDomain } from './DomainContext';
import { derivePageState } from './pageStatus';
import useMediaQuery from './editor/useMediaQuery';
import AutoTextarea from './editor/AutoTextarea';
import SidebarCard from './editor/SidebarCard';
import PublishCard from './editor/PublishCard';
import DetailsCard from './editor/DetailsCard';
import SeoCard from './editor/SeoCard';
import TaxonomyCard from './editor/TaxonomyCard';
import AuthorsCard from './editor/AuthorsCard';
import MainImageCard from './editor/MainImageCard';
import AdStatsCard from './editor/AdStatsCard';
import SearchCard from './editor/SearchCard';
import ProductsCard from './editor/ProductsCard';
import SectionList from './editor/SectionList';
import TimelinePanel from './timeline/TimelinePanel';
import { getDefaultSectionData } from './editor/sectionTypes';

/*
 * Page editor container. Owns the page draft, dirty tracking, save/publish,
 * and the section CRUD; the fields themselves live in ./editor/*.
 *
 * Wire contracts kept from earlier rounds (do not change without the backend):
 *   - PUT/POST payload is the whole page object plus status/published_at.
 *   - main_image.img_filename === 'Processing...' asks the backend to copy the
 *     chosen media into the website bucket on the next save.
 *   - Sections: create({type, order, data, settings}) then reorder(body ids);
 *     update sends the full section object; body order is refetched after each.
 *
 * Revisions. A page that is live (published, date not in the future) is not
 * edited in place: Save writes a pending revision (page_drafts/<slug>, with its
 * own sections), which the sites show only in preview. The first edit of any
 * kind starts the revision (copying the live sections), and from then on the
 * page fields and every section call target it ({revision: true}). "Publish
 * now" copies it over the live page; "Discard revision" drops it. Drafts and
 * scheduled pages are not public yet, so they keep saving directly.
 */

/* Stats the crons write onto the live page; a revision's copies are stale. */
const LIVE_STATS = ['ad_impressions_daily', 'ad_clicks_daily', 'ad_impressions_total', 'ad_clicks_total', 'ad_stats_updated_at'];

const EMPTY_IMAGE = {
  img_filename: '', img_original_filename: '', img_alt: '', img_caption: '', source: '', media_id: '', source_author: '',
};

const emptyPage = () => ({
  title: '', subtitle: '', slug: '', excerpt: '', description: '', introduction_md: '', body: [], notes: '',
  main_image: { ...EMPTY_IMAGE },
  primary_topic: '', parent_page: '', related_pages: [], has_children: false, hide_main_image: false,
  shareable_quotes: [], tags: [], 'key-points': [], page_type: '', status: 'draft', published_at: null,
});

const hydrate = (p, pageId) => ({
  ...emptyPage(),
  title: p.title || '',
  subtitle: p.subtitle || '',
  slug: p.slug || pageId,
  excerpt: p.excerpt || '',
  description: p.description || '',
  introduction_md: p.introduction_md || '',
  body: p.body || [],
  notes: p.notes || '',
  main_image: p.main_image || { ...EMPTY_IMAGE },
  primary_topic: p.primary_topic || '',
  parent_page: p.parent_page || '',
  related_pages: p.related_pages || [],
  has_children: p.has_children || false,
  hide_main_image: p.hide_main_image || false,
  shareable_quotes: p.shareable_quotes || [],
  tags: p.tags || [],
  'key-points': p['key-points'] || [],
  page_type: p.page_type || '',
  status: p.status || 'draft',
  published_at: p.published_at || null,
  ad_impressions_daily: p.ad_impressions_daily || null,
  ad_clicks_daily: p.ad_clicks_daily || null,
  ad_impressions_total: p.ad_impressions_total || null,
  ad_clicks_total: p.ad_clicks_total || null,
  ad_stats_updated_at: p.ad_stats_updated_at || null,
  // Only carried when the page has it: the backend writes author_slugs only
  // when the key is sent, so pages without a byline are left untouched.
  ...(Array.isArray(p.author_slugs) ? { author_slugs: p.author_slugs } : {}),
  // Read-only: decides the "Show map" default while show_map is unset.
  source_page_type: p.source_page_type || '',
  // Likewise only when set, so saving a page leaves an unset flag unset.
  ...(typeof p.show_map === 'boolean' ? { show_map: p.show_map } : {}),
});

const slugify = (text) => text
  .toLowerCase()
  .trim()
  .replace(/\s+/g, '-')
  .replace(/[^a-z0-9-/]/g, '')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || e.message || String(e)).join(', ');
  if (detail) return JSON.stringify(detail);
  return err?.message || fallback;
};

/* Field key -> [input id, sidebar card that must be open to reach it]. */
const ERROR_TARGETS = [
  ['title', 'page-title', null],
  ['slug', 'page-slug', 'details'],
  ['primary_topic', 'page-primary-topic', 'details'],
];

const validatePage = (draft) => {
  const errs = {};
  if (!(draft.title || '').trim()) errs.title = 'Title is required';
  if (!(draft.slug || '').trim()) errs.slug = 'Slug is required';
  if (draft.main_image?.img_original_filename && !(draft.primary_topic || '').trim()) {
    errs.primary_topic = 'Primary topic is required when an image is set';
  }
  return errs;
};

const PageEditor = () => {
  const { domainId, pageId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const domainCtx = useDomain();
  const wide = useMediaQuery('(min-width: 1100px)');
  const isNewPage = !pageId || pageId === 'new';

  const [page, setPage] = useState(emptyPage);
  const [availablePages, setAvailablePages] = useState([]);
  const [sections, setSections] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [dirtySections, setDirtySections] = useState(() => new Set());
  const [savedVersions, setSavedVersions] = useState({});
  const [sectionsBusy, setSectionsBusy] = useState(false);
  const [cardOpen, setCardOpen] = useState({ publish: true, details: true, seo: true, taxonomy: true, authors: true, notes: true, products: true, adstats: false, search: true });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [mainImageUrl, setMainImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  /* The server's thin verdict, as of the last load or save. */
  const [thin, setThin] = useState(null);
  /* The live page's state at load: 'published' puts the editor in revision mode. */
  const [liveState, setLiveState] = useState(null);
  /* A pending revision exists. Mirrored in a ref so handlers that start one
     and then keep going see it without waiting for a render. */
  const [hasRevision, setHasRevisionState] = useState(false);
  const revisionRef = useRef(false);
  const setHasRevision = (value) => { revisionRef.current = value; setHasRevisionState(value); };
  const [reloadKey, setReloadKey] = useState(0);
  const saveRef = useRef(null);

  const anyDirty = dirty || dirtySections.size > 0;
  /* The live page is public right now, so edits go to a revision. */
  const revisionMode = !isNewPage && liveState === 'published';

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const pagesData = await getPageSummaries(domainId);
        if (cancelled) return;
        setAvailablePages(pagesData || []);

        if (!isNewPage) {
          const livePage = await getPage(domainId, pageId);
          if (cancelled) return;
          // With a pending revision, the editor works on it, not on the live page.
          let pageData = livePage;
          const pending = !!livePage.has_revision;
          if (pending) {
            const rev = await getPageRevision(domainId, pageId);
            if (cancelled) return;
            pageData = { ...rev, status: livePage.status };
            LIVE_STATS.forEach((k) => { pageData[k] = livePage[k]; });
          }
          revisionRef.current = pending;
          setHasRevisionState(pending);
          setLiveState(derivePageState(livePage));
          setPage(hydrate(pageData, pageId));
          setThin(livePage.thin || null);

          if (pageData.main_image?.media_id) {
            try {
              const url = await getMediaUrl(pageData.main_image.media_id);
              if (!cancelled) { setMainImageUrl(url); setImageError(null); }
            } catch (err) {
              if (!cancelled) { setMainImageUrl(null); setImageError(getErrorMessage(err, 'Could not load the image preview.')); }
            }
          } else {
            setMainImageUrl(null);
            setImageError(null);
          }

          try {
            const sectionsData = await getPageSections(domainId, pageId, { revision: pending });
            if (cancelled) return;
            const list = sectionsData || [];
            setSections(list);
            // Short pages open every section; long ones start collapsed.
            const inBody = (pageData.body || []).filter((id) => list.some((s) => s.id === id));
            setExpandedIds(new Set(inBody.length <= 6 ? inBody : []));
          } catch (sectionErr) {
            console.warn('Failed to fetch sections:', sectionErr);
            if (!cancelled) setSections([]);
          }
        } else {
          setPage(emptyPage());
          setThin(null);
          revisionRef.current = false;
          setHasRevisionState(false);
          setLiveState(null);
          setSections([]);
          setExpandedIds(new Set());
          setMainImageUrl(null);
          setImageError(null);
        }

        setDirty(false);
        setDirtySections(new Set());
        setFieldErrors({});
        setSlugTouched(false);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) navigate('/login');
        else setError(getErrorMessage(err, 'Failed to fetch page data'));
        setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { cancelled = true; };
  }, [domainId, pageId, isNewPage, navigate, reloadKey]);

  useEffect(() => { window.scrollTo(0, 0); }, [pageId]);

  // Ctrl/Cmd+S saves; the ref keeps the listener bound once with a fresh closure.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === 's') {
        e.preventDefault();
        if (saveRef.current) saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!anyDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [anyDirty]);

  // ── Page fields ─────────────────────────────────────────────────────────
  const handleInputChange = (field, value) => {
    if (field === 'slug' && isNewPage) {
      value = slugify(value);
      setSlugTouched(true);
    }
    // Until the slug is edited by hand, a new page's slug follows its title.
    const derivedSlug = field === 'title' && isNewPage && !slugTouched ? slugify(value) : null;

    setPage((prev) => {
      if (field.startsWith('main_image.')) {
        const nested = field.split('.')[1];
        return { ...prev, main_image: { ...(prev.main_image || {}), [nested]: value } };
      }
      return { ...prev, [field]: value, ...(derivedSlug !== null ? { slug: derivedSlug } : {}) };
    });
    setDirty(true);
    if (fieldErrors[field] || (derivedSlug !== null && fieldErrors.slug)) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        if (derivedSlug !== null) delete next.slug;
        return next;
      });
    }
  };

  const toggleCard = (key) => setCardOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const focusFirstError = (errs) => {
    const hit = ERROR_TARGETS.find(([key]) => errs[key]);
    if (!hit) return;
    const [, id, card] = hit;
    if (card) setCardOpen((prev) => ({ ...prev, [card]: true }));
    window.setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.focus({ preventScroll: true });
    }, 0);
  };

  // ── Save / publish ──────────────────────────────────────────────────────
  const handleSave = async (overrides = {}) => {
    if (saving) return;
    const draft = { ...page, ...overrides };

    const errs = validatePage(draft);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Fix the highlighted fields before saving.');
      focusFirstError(errs);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = { ...draft, status: draft.status || 'draft', published_at: draft.published_at || null };

      if (revisionMode) {
        const res = await savePageRevision(domainId, pageId, payload);
        setHasRevision(true);
        setDirty(false);
        setLastSavedAt(new Date());
        addToast(res?.created ? 'Saved as a revision. The live page is unchanged until you publish.' : 'Revision saved', 'success');
        await resyncFrom(getPageRevision(domainId, pageId));
        return true;
      }

      if (isNewPage) {
        const response = await createPage(domainId, payload);
        setDirty(false);
        setLastSavedAt(new Date());
        addToast('Page created', 'success');
        navigate(`/domain/${domainId}/pages/${response.page_id}/edit`, { replace: true });
      } else {
        await updatePage(domainId, pageId, payload);
        setDirty(false);
        setLastSavedAt(new Date());
        addToast('Saved', 'success');
        await resyncFrom(getPage(domainId, pageId), true);
      }
      return true;
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else setError(getErrorMessage(err, 'Failed to save page'));
      return false;
    } finally {
      setSaving(false);
    }
  };
  saveRef.current = handleSave;

  // Re-sync what the server may have rewritten: published_at is filled on
  // publish and a "Processing..." image gets its real filename. Leaving them
  // stale would re-trigger processing on the next save.
  const resyncFrom = async (freshPromise, withThin = false) => {
    try {
      const fresh = await freshPromise;
      if (withThin) setThin(fresh.thin || null);
      setPage((prev) => ({
        ...prev,
        status: fresh.status || prev.status,
        published_at: fresh.published_at || null,
        body: fresh.body || prev.body,
        main_image: fresh.main_image || prev.main_image,
      }));
      if (fresh.main_image?.media_id && fresh.main_image.media_id !== page.main_image?.media_id) {
        setMainImageUrl(await getMediaUrl(fresh.main_image.media_id));
      }
    } catch (syncErr) {
      console.warn('Saved, but could not re-sync page:', syncErr);
    }
  };

  // ── Revisions ───────────────────────────────────────────────────────────
  /* The document the section calls work on: the revision once one exists. */
  const sectionOpts = () => (revisionRef.current ? { revision: true } : undefined);
  const loadDoc = () => (revisionRef.current ? getPageRevision(domainId, pageId) : getPage(domainId, pageId));

  /* On a live page, any section edit first starts the revision (it copies the
     live sections, ids and all), so nothing an editor does here touches the
     live page until they publish. Field edits made so far go into it too. */
  const ensureRevision = async () => {
    if (!revisionMode || revisionRef.current) return;
    await savePageRevision(domainId, pageId, { ...page, status: page.status || 'published', published_at: page.published_at || null });
    setHasRevision(true);
    setDirty(false);
    addToast('Started a revision. The live page is unchanged until you publish.', 'info');
  };

  const handlePublishRevision = () => {
    if (dirtySections.size > 0) {
      setError('Save or close the open section edits before publishing.');
      return;
    }
    setConfirm({
      title: 'Publish this revision?',
      message: 'The revision replaces the live page, sections included, for every visitor. The revision is then removed.',
      confirmLabel: 'Publish now',
      danger: false,
      onConfirm: async () => {
        setConfirm(null);
        if (dirty || !revisionRef.current) {
          const ok = await handleSave();
          if (!ok) return;
        }
        setSaving(true);
        try {
          await publishPageRevision(domainId, pageId);
          setHasRevision(false);
          addToast('Revision published', 'success');
          setReloadKey((k) => k + 1);
        } catch (err) {
          if (err.response?.status === 401) navigate('/login');
          else setError(getErrorMessage(err, 'Failed to publish the revision'));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleDiscardRevision = () => {
    setConfirm({
      title: 'Discard this revision?',
      message: 'Every edit saved in the revision, sections included, is thrown away. The live page stays as it is.',
      confirmLabel: 'Discard revision',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setSaving(true);
        try {
          await discardPageRevision(domainId, pageId);
          setHasRevision(false);
          setDirty(false);
          setDirtySections(new Set());
          addToast('Revision discarded', 'success');
          setReloadKey((k) => k + 1);
        } catch (err) {
          if (err.response?.status === 401) navigate('/login');
          else setError(getErrorMessage(err, 'Failed to discard the revision'));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleSitePreview = () => {
    if (anyDirty) addToast('Preview shows what is saved. Unsaved edits are not in it.', 'info');
    openSitePreview(domainId, page.slug || pageId).catch((err) => {
      setError(getErrorMessage(err, 'Could not open the preview'));
    });
  };

  const handlePublishNow = () => {
    // The sites show published_at as the article date and order feeds by it,
    // so a draft that already carries a past date keeps it; only an empty or
    // future date is replaced with "now".
    const existing = page.published_at ? new Date(page.published_at) : null;
    const keepExisting = existing && !Number.isNaN(existing.getTime()) && existing.getTime() <= Date.now();
    const overrides = { status: 'published', published_at: keepExisting ? page.published_at : new Date().toISOString() };
    setPage((prev) => ({ ...prev, ...overrides }));
    handleSave(overrides);
  };

  const guardedNavigate = (to) => {
    if (!anyDirty) { navigate(to); return; }
    setConfirm({
      title: 'Discard unsaved changes?',
      message: dirtySections.size > 0
        ? 'This page has unsaved edits, including sections that were not saved. Leave without saving?'
        : 'This page has edits that have not been saved. Leave without saving?',
      confirmLabel: 'Discard changes',
      danger: true,
      onConfirm: () => { setConfirm(null); setDirty(false); setDirtySections(new Set()); navigate(to); },
    });
  };

  // ── Main image ──────────────────────────────────────────────────────────
  const handleRemoveMainImage = () => {
    setConfirm({
      title: 'Remove main image?',
      message: 'This deletes the image file from storage and cannot be undone.',
      confirmLabel: 'Remove image',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          setError(null);
          await removeMainImage(domainId, pageId);
          // Drop the attribute entirely so the next save does not re-send it.
          setPage((prev) => {
            const next = { ...prev };
            delete next.main_image;
            return next;
          });
          setMainImageUrl(null);
          setImageError(null);
          addToast('Main image removed', 'success');
        } catch (err) {
          if (err.response?.status === 401) navigate('/login');
          else setError(getErrorMessage(err, 'Failed to remove main image'));
        }
      },
    });
  };

  const handleClearMainImage = () => {
    setPage((prev) => ({ ...prev, main_image: { ...EMPTY_IMAGE } }));
    setMainImageUrl(null);
    setImageError(null);
    setDirty(true);
  };

  const handleMainImagePicked = (image) => {
    setPage((prev) => ({
      ...prev,
      main_image: {
        media_id: image._id,
        img_original_filename: image.original_filename || image.name || '',
        img_filename: 'Processing...', // backend copies + renames the file on save
        img_alt: image.description || '',
        img_caption: '',
        source: image.source || '',
        source_author: image.contributor_name || '',
      },
    }));
    setDirty(true);
    setShowImagePicker(false);
  };

  // ── Sections ────────────────────────────────────────────────────────────
  const refreshSections = async () => {
    const [sectionsData, pageData] = await Promise.all([getPageSections(domainId, pageId, sectionOpts()), loadDoc()]);
    setSections(sectionsData || []);
    if (!revisionRef.current) setThin(pageData.thin || null);
    setPage((prev) => ({ ...prev, body: pageData.body || [] }));
    return { sectionsData: sectionsData || [], body: pageData.body || [] };
  };

  const syncBody = async (newBody) => {
    const previous = page.body;
    setPage((prev) => ({ ...prev, body: newBody }));
    setSectionsBusy(true);
    try {
      await ensureRevision();
      await reorderPageSections(domainId, pageId, newBody, sectionOpts());
      const pageData = await loadDoc();
      setPage((prev) => ({ ...prev, body: pageData.body || newBody }));
    } catch (err) {
      setPage((prev) => ({ ...prev, body: previous }));
      setError(getErrorMessage(err, 'Failed to reorder sections'));
    } finally {
      setSectionsBusy(false);
    }
  };

  const handleCreateSection = async (type, position) => {
    if (isNewPage) { setError('Save the page before adding sections'); return; }
    setSectionsBusy(true);
    setError(null);
    try {
      await ensureRevision();
      const created = await createPageSection(domainId, pageId, {
        type,
        order: position,
        data: getDefaultSectionData(type),
        settings: { visible: true, padding: 'normal' },
      }, sectionOpts());
      const newId = created && created.section_id;
      // The server inserts into the body itself; pull it back and only reorder
      // if it did not land where the "+" was clicked.
      const { body } = await refreshSections();
      if (newId) {
        const desired = body.filter((id) => id !== newId);
        desired.splice(Math.min(position, desired.length), 0, newId);
        if (desired.join('\n') !== body.join('\n')) {
          await reorderPageSections(domainId, pageId, desired, sectionOpts());
          setPage((prev) => ({ ...prev, body: desired }));
        }
        setExpandedIds((prev) => new Set(prev).add(newId));
        window.setTimeout(() => {
          document.getElementById(`section-${newId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 50);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create section'));
    } finally {
      setSectionsBusy(false);
    }
  };

  const handleDeleteSection = (sectionId) => {
    setConfirm({
      title: 'Delete this section?',
      message: 'The section and its content are removed permanently. This cannot be undone.',
      confirmLabel: 'Delete section',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setSectionsBusy(true);
        try {
          await ensureRevision();
          await deletePageSection(domainId, pageId, sectionId, sectionOpts());
          await refreshSections();
          addToast('Section deleted', 'success');
        } catch (err) {
          setError(getErrorMessage(err, 'Failed to delete section'));
        } finally {
          setSectionsBusy(false);
        }
      },
    });
  };

  const handleMoveSection = (from, to) => {
    if (to < 0 || to >= page.body.length) return;
    const newBody = [...page.body];
    const [moved] = newBody.splice(from, 1);
    newBody.splice(to, 0, moved);
    syncBody(newBody);
  };

  const handleAddOrphan = (sectionId) => syncBody([...page.body, sectionId]);

  const handleSaveSection = async (section, draft) => {
    setSectionsBusy(true);
    try {
      await ensureRevision();
      await updatePageSection(domainId, pageId, section.id, { ...section, ...draft }, sectionOpts());
      const sectionsData = await getPageSections(domainId, pageId, sectionOpts());
      setSections(sectionsData || []);
      setSavedVersions((prev) => ({ ...prev, [section.id]: (prev[section.id] || 0) + 1 }));
      addToast('Section saved', 'success');
    } catch (err) {
      const msg = getErrorMessage(err, 'Failed to update section');
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setSectionsBusy(false);
    }
  };

  const handleSectionDirty = useCallback((id, isDirty) => {
    setDirtySections((prev) => {
      if (prev.has(id) === isDirty) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const toggleSection = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page" style={{ textAlign: 'center', marginTop: 40 }}>
        <Spinner size={40} label="Loading…" />
      </div>
    );
  }

  const pageState = derivePageState(page);
  const canPreview = !isNewPage && !!page.slug && previewEnabled();
  const hostname = (domainCtx?.domains || []).find((d) => d.id === domainId)?.hostname || domainId.replace(/_/g, '.');
  const liveUrl = !isNewPage && page.slug ? `https://${hostname}/${page.slug}` : null;
  const firestoreUrl = !isNewPage && page.slug
    ? `https://console.cloud.google.com/firestore/databases/new-fastapi-websites-datastore/data/panel/domains/${domainId}/pages/${page.slug}?project=hosting-for-digital-projects`
    : null;

  const publishCard = (
    <PublishCard
      page={page}
      pageState={pageState}
      isNewPage={isNewPage}
      saving={saving}
      dirty={anyDirty}
      lastSavedAt={lastSavedAt}
      canPreview={canPreview}
      liveUrl={liveUrl}
      open={cardOpen.publish}
      onToggle={() => toggleCard('publish')}
      onChange={handleInputChange}
      onSave={() => handleSave()}
      onPublishNow={handlePublishNow}
      onPreview={() => setShowPreview(true)}
      onSitePreview={isNewPage ? undefined : handleSitePreview}
      revisionMode={revisionMode}
      hasRevision={hasRevision}
      onPublishRevision={handlePublishRevision}
      onDiscardRevision={handleDiscardRevision}
    />
  );

  return (
    <div className="page" style={{ maxWidth: wide ? 1220 : 860 }}>
      {/* ── Sticky bar: Back, title, state, unsaved marker, Save ─────────── */}
      <div
        style={{
          position: 'sticky', top: 'var(--app-header-h, 0px)', zIndex: 100,
          background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
          margin: '-24px -20px 20px', padding: '10px 20px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => guardedNavigate(`/domain/${domainId}/pages`)}>
          ← Back
        </button>
        <h1
          style={{ margin: 0, flex: '1 1 160px', minWidth: 0, fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={page.title || page.slug}
        >
          {page.title || (isNewPage ? 'New page' : page.slug)}
        </h1>
        <Badge state={pageState} />
        {hasRevision && (
          <Badge state="info" title="Saved edits are waiting in a revision: visible in preview only until published">Pending revision</Badge>
        )}
        {anyDirty && <Badge state="warn" title={dirtySections.size > 0 ? `${dirtySections.size} section(s) unsaved` : 'Unsaved changes'}>Unsaved</Badge>}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => handleSave()}
          disabled={saving}
          title={revisionMode ? 'Save as a pending revision; the live page is unchanged (Ctrl+S / Cmd+S)' : 'Save (Ctrl+S / Cmd+S)'}
        >
          {saving ? 'Saving…' : revisionMode ? 'Save as revision' : 'Save'}
        </button>
      </div>

      {error && <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>}
      {!isNewPage && <ThinBanner verdict={thin} />}

      <div style={wide ? { display: 'grid', gridTemplateColumns: 'minmax(0, 820px) 320px', gap: 24, alignItems: 'start', justifyContent: 'center' } : undefined}>
        {!wide && <div style={{ marginBottom: 16 }}>{publishCard}</div>}

        {/* ── Main column ─────────────────────────────────────────────── */}
        <div style={{ minWidth: 0 }}>
          <section className="card" aria-label="Page content">
            <div className="field" style={{ marginBottom: 6 }}>
              <label htmlFor="page-title" className="sr-only">Title</label>
              <input
                id="page-title"
                type="text"
                value={page.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Page title"
                aria-invalid={!!fieldErrors.title}
                aria-describedby={fieldErrors.title ? 'page-title-error' : undefined}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 30, fontWeight: 700, lineHeight: 1.2,
                  border: 'none', borderBottom: `2px solid ${fieldErrors.title ? 'var(--color-danger)' : 'transparent'}`,
                  outline: 'none', padding: '4px 0', background: 'transparent', color: 'var(--color-text)',
                }}
              />
              {fieldErrors.title && <span className="error" id="page-title-error" role="alert">{fieldErrors.title}</span>}
            </div>
            <div className="field">
              <label htmlFor="page-subtitle" className="sr-only">Subtitle</label>
              <input
                id="page-subtitle"
                type="text"
                value={page.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                placeholder="Subtitle (optional)"
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 18, color: 'var(--color-muted)',
                  border: 'none', outline: 'none', padding: '2px 0', background: 'transparent',
                }}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="page-introduction">Introduction <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>(Markdown)</span></label>
              <AutoTextarea
                id="page-introduction"
                value={page.introduction_md}
                onChange={(e) => handleInputChange('introduction_md', e.target.value)}
                placeholder="Opening paragraphs, in Markdown…"
                minRows={8}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 14 }}
              />
              <span className="hint">**bold**, *italic*, # headings, [links](https://…)</span>
            </div>
          </section>

          <div style={{ marginTop: 16 }}>
            <MainImageCard
              page={page}
              isNewPage={isNewPage}
              mainImageUrl={mainImageUrl}
              imageError={imageError}
              onChange={handleInputChange}
              onPick={() => setShowImagePicker(true)}
              // Remove deletes the file and edits the live page; on a revision
              // it only clears the image from the revision.
              onRemove={revisionMode ? handleClearMainImage : handleRemoveMainImage}
              onClear={handleClearMainImage}
            />
          </div>

          <div style={{ marginTop: 24 }}>
            <SectionList
              isNewPage={isNewPage}
              body={page.body}
              sections={sections}
              expandedIds={expandedIds}
              onToggle={toggleSection}
              onExpandAll={() => setExpandedIds(new Set(page.body))}
              onCollapseAll={() => setExpandedIds(new Set())}
              onCreate={handleCreateSection}
              onMove={handleMoveSection}
              onDelete={handleDeleteSection}
              onSave={handleSaveSection}
              onDirtyChange={handleSectionDirty}
              onAddOrphan={handleAddOrphan}
              busy={sectionsBusy}
              savedVersions={savedVersions}
            />
          </div>
        </div>

        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <aside
          aria-label="Page settings"
          style={wide
            ? { position: 'sticky', top: 'calc(var(--app-header-h, 0px) + 64px)', maxHeight: 'calc(100vh - var(--app-header-h, 0px) - 80px)', overflowY: 'auto', paddingRight: 2 }
            : { marginTop: 24 }}
        >
          {wide && publishCard}
          <DetailsCard
            page={page}
            isNewPage={isNewPage}
            pageId={pageId}
            availablePages={availablePages}
            errors={fieldErrors}
            open={cardOpen.details}
            onToggle={() => toggleCard('details')}
            onChange={handleInputChange}
            onEditParent={() => guardedNavigate(`/domain/${domainId}/pages/${encodeURIComponent(page.parent_page)}/edit`)}
            firestoreUrl={firestoreUrl}
          />
          {!isNewPage && (
            <SearchCard domainId={domainId} slug={page.slug || pageId} open={cardOpen.search} onToggle={() => toggleCard('search')} />
          )}
          <SeoCard page={page} open={cardOpen.seo} onToggle={() => toggleCard('seo')} onChange={handleInputChange} />
          <TaxonomyCard page={page} open={cardOpen.taxonomy} onToggle={() => toggleCard('taxonomy')} onChange={handleInputChange} onNavigate={guardedNavigate} />
          <AuthorsCard domainId={domainId} value={page.author_slugs} onChange={(next) => handleInputChange('author_slugs', next)} open={cardOpen.authors} onToggle={() => toggleCard('authors')} />
          {/* The old free-text notes box is now this page's timeline; its text was copied in as the first entry. */}
          <SidebarCard id="notes" title="Notes & follow-ups" open={cardOpen.notes} onToggle={() => toggleCard('notes')}>
            {isNewPage ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Save the page first, then log notes and follow-ups here.</p>
            ) : (
              <TimelinePanel domainId={domainId} page={pageId} compact />
            )}
          </SidebarCard>
          {!isNewPage && (
            <ProductsCard domainId={domainId} slug={page.slug || pageId} open={cardOpen.products} onToggle={() => toggleCard('products')} />
          )}
          {!isNewPage && <AdStatsCard page={page} open={cardOpen.adstats} onToggle={() => toggleCard('adstats')} />}
        </aside>
      </div>

      {showImagePicker && <ImagePicker onSelect={handleMainImagePicked} onClose={() => setShowImagePicker(false)} />}

      <ConfirmDialog
        open={!!confirm}
        title={confirm ? confirm.title : ''}
        message={confirm ? confirm.message : ''}
        confirmLabel={confirm ? confirm.confirmLabel : 'Confirm'}
        danger={!!(confirm && confirm.danger)}
        onConfirm={confirm ? confirm.onConfirm : () => {}}
        onCancel={() => setConfirm(null)}
      />

      {showPreview && page.slug && (
        <PagePreviewModal domainId={domainId} slug={page.slug} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

export default PageEditor;
